import React, { useState } from 'react';

const SourceCitation = ({ index, source }) => {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const relevanceScore = source.relevance_score || Math.round((source.relevance || 0) * 100);
  const framework = source.framework || source.authority_info?.abbreviation || '';
  const authority = source.authority_info?.abbreviation || 'EUR-Lex';
  const pdfUrl = source.pdf_url;
  const sourcePageUrl = source.source_page_url;
  const reference = source.reference;
  const documentType = source.document_type;
  const sections = source.sections || [];

  const getAuthorityClass = (auth) => {
    const authUpper = (auth || '').toUpperCase();
    if (authUpper === 'ESMA') return 'auth-esma-ultra';
    if (authUpper === 'EBA') return 'auth-eba-ultra';
    if (authUpper === 'EIOPA') return 'auth-eiopa-ultra';
    if (authUpper === 'ENISA') return 'auth-enisa-ultra';
    if (authUpper === 'EDPB') return 'auth-edpb-ultra';
    return 'auth-default-ultra';
  };

  const handleViewOnEsma = (e) => {
    e.stopPropagation();
    if (sourcePageUrl) {
      window.open(sourcePageUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDownloadPdf = (e) => {
    e.stopPropagation();
    if (pdfUrl) {
      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleSaveToVault = async (e) => {
    e.stopPropagation();

    if (!pdfUrl) {
      alert('PDF URL not available');
      return;
    }

    // Get user context from localStorage
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id;
    const companyId = user.company_id || user.company_kyb?.[0];

    if (!userId || !companyId) {
      alert('Please log in to save documents to vault');
      return;
    }

    setSaving(true);

    try {
      const response = await fetch('https://compliance-api-func-dev.azurewebsites.net/api/vault/save-from-url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          company_id: companyId,
          pdf_url: pdfUrl,
          title: source.title || source.name || 'Unknown Source',
          reference: reference,
          document_type: documentType,
          source_page_url: sourcePageUrl,
          framework: framework,
          sections: sections,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000); // Reset after 3 seconds
      } else {
        alert(`Failed to save: ${data.error}`);
      }
    } catch (error) {
      console.error('Save to vault error:', error);
      alert('Failed to save document to vault');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="source-row-ultra">
      <div className="source-header-ultra">
        <span className={`source-auth-ultra ${getAuthorityClass(authority)}`}>
          {authority}
        </span>
        {reference && (
          <span className="source-ref-badge-ultra">{reference}</span>
        )}
        {framework && (
          <span className="fw-tag-ultra">{framework}</span>
        )}
        {documentType && (
          <span className="doc-type-badge-ultra">{documentType}</span>
        )}
      </div>

      <span className="source-title-ultra">
        {source.title || source.name || 'Unknown Source'}
      </span>

      {sections.length > 0 && (
        <div className="source-sections-ultra">
          {sections.slice(0, 3).map((section, idx) => (
            <span key={idx} className="section-chip-ultra">{section}</span>
          ))}
          {sections.length > 3 && (
            <span className="section-chip-ultra">+{sections.length - 3} more</span>
          )}
        </div>
      )}

      <div className="source-actions-ultra">
        <span className="source-score-ultra">{relevanceScore}%</span>

        {sourcePageUrl && (
          <button
            className="action-btn-ultra view-btn-ultra"
            onClick={handleViewOnEsma}
            title="View on ESMA website"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
              <polyline points="15 3 21 3 21 9"/>
              <line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
            View on ESMA
          </button>
        )}

        {pdfUrl && (
          <button
            className="action-btn-ultra download-btn-ultra"
            onClick={handleDownloadPdf}
            title="Download PDF"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
            </svg>
            Download PDF
          </button>
        )}

        {pdfUrl && (
          <button
            className={`action-btn-ultra save-btn-ultra ${saved ? 'saved' : ''}`}
            onClick={handleSaveToVault}
            disabled={saving || saved}
            title="Save to Vault"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {saved ? (
                <polyline points="20 6 9 17 4 12"/>
              ) : (
                <>
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                  <polyline points="17 21 17 13 7 13 7 21"/>
                  <polyline points="7 3 7 8 15 8"/>
                </>
              )}
            </svg>
            {saving ? 'Saving...' : saved ? 'Saved' : 'Save to Vault'}
          </button>
        )}
      </div>
    </div>
  );
};

export default SourceCitation;
